import * as CryptoJS from 'crypto-js';

type SizeUnit = "B" | "KB" | "MB" | "GB" | "TB";

type ContentMd5 = { md5: string, content: ArrayBuffer };

export namespace file {
  const fileUnits = ['B', 'KB', 'MB', 'GB', 'TB'];

  /**
   * @param size
   * @param sizeUnit size单位
   * @param targetUnit 目标单位
   * @description 单位转换
   */
  export function unitConverter(size: number, unit: SizeUnit, targetUnit: SizeUnit): number | null {
    if (size) {
      if (unit == targetUnit) {
        return size;
      }

      let sizeIndex = fileUnits.indexOf(unit);
      let targetIndex = fileUnits.indexOf(targetUnit);

      let times = Math.pow(1024, sizeIndex - targetIndex);
      return Number((size * times).toFixed(2));
    }
    return null;
  }

  /**
   * @param fileSize
   * @param unit 指定格式化单位
   * @description 文件大小单位格式化
   */
  export function formatFileSize(fileSize: number, unit?: SizeUnit): string {
    if (fileSize) {
      if (unit && fileUnits.indexOf(unit) > -1) {
        let index = fileUnits.indexOf(unit);
        let times = Math.pow(1024, index);

        let size = fileSize / times;
        let displaySize = size.toFixed(2).toString() + ` ${fileUnits[index]}`;
        return displaySize;
      } else {
        let size = fileSize;
        let unitIndex = 0;

        while (size >= 1000 && unitIndex < fileUnits.length - 1) {
          size /= 1024;
          unitIndex++;
        }
        let displaySize = size.toFixed(2).toString() + ` ${fileUnits[unitIndex]}`;
        return displaySize;
      }
    }
    return "";
  }

  /**
   * NOTE - 使用 FileReader 解析 File后的内容生成相应的MD5
   * @param file
   * @param chunkSize
   * @returns
   */
  export function getContentMD5(file: File, chunkSize: number = 1024 * 1024): Promise<ContentMd5> {
    return new Promise<ContentMd5>((resolve, reject) => {
      try {
        readAsArrayBuffer(file).then((content: ArrayBuffer) => {
          //CryptoJS 官方教程 https://cryptojs.gitbook.io/docs
          //git仓库地址 https://github.com/brix/crypto-js
          const hash = CryptoJS.algo.MD5.create();
          let offset = 0;

          while (offset < content.byteLength) {
            const slice = content.slice(offset, offset + chunkSize)
            const wordArray = CryptoJS.lib.WordArray.create(slice);
            hash.update(wordArray);
            offset += chunkSize;
          }
          let token = hash.finalize().toString();
          resolve({ md5: token, content: content });
        }, (error) => {
          reject(error);
        })
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * NOTE - 通过 FileReader 解析 File文件对象
   * @param file
   * @returns
   */
  export function readAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise<ArrayBuffer>((resolve, reject) => {
      //LINK - https://developer.mozilla.org/zh-CN/docs/Web/API/FileReader
      //* 接口允许 Web 应用程序异步读取存储在用户计算机上的文件（或原始数据缓冲区）的内容，使用 File 或 Blob 对象指定要读取的文件或数据。
      let reader = new FileReader();
      reader.onloadend = function (event) {
        //TODO - 需要断言
        if (event && event.target && event.target.result) {
          if (typeof event.target.result == "string") {
            //字符转ArrayBuffer这段还没测试过的，如果有问题请改正
            let buffer = new ArrayBuffer(event.target.result.length * 2); // 2 bytes for each char
            var bufferArr = new Uint16Array(buffer);
            for (var i = 0, strLen = event.target.result.length; i < strLen; i++) {
              bufferArr[i] = event.target.result.charCodeAt(i);
            }
            resolve(buffer);
          } else {
            resolve(event.target.result);
          }
        } else {
          reject(event);
        }
      }

      reader.onerror = function (error) {
        reject(error);
      }

      reader.readAsArrayBuffer(file);
    })
  }

  /**
   * @param fileName
   * @returns 获取文件后缀名
   */
  export function getFileSuffix(fileName: string): string | null {
    let suffix: string | null = null;
    if (fileName) {
      let nameList = fileName.split(".");
      if (nameList.length >= 2) {
        suffix = nameList[nameList.length - 1];
      }
    }
    return suffix;
  }

  /**
   * @param files 
   * @description 将 files 转为数组类型
   */
  export function getFileList(files: FileList): Array<File> | null {
    let newFiles: Array<File> = [];
    if (files) {
      for (const itemKey in files) {
        if (Object.prototype.hasOwnProperty.call(files, itemKey)) {
          let item = files[itemKey];
          item && newFiles.push(item);
        }
      }
    }
    return newFiles.length > 0 ? newFiles : null;
  }

}
